import { throttle } from "lodash";
import { useEffect, useRef } from "react";

// this approach from https://github.com/molefrog/wouter/blob/master/use-location.js
const eventPopstate = "popstate";
const eventPushState = "pushState";
const eventReplaceState = "replaceState";

const historyEvents = [eventPopstate, eventPushState, eventReplaceState];

export type Area = "edit" | "docs" | "auth";

if (typeof history !== "undefined") {
  for (const type of [eventPushState, eventReplaceState]) {
    const original = (history as any)[type];

    (history as any)[type] = function (...args: any) {
      const result = original.apply(this, args);
      const event = new Event(type);
      (event as any).arguments = args;

      dispatchEvent(event);
      return result;
    };
  }
}

const getRudderAnalytics = () => {
  if ("rudderanalytics" in window) {
    return (window as any).rudderanalytics;
  }
};

export const usePageTracking = (getProperties: (() => Properties) | undefined = undefined) => {
  const currentPath = useRef<string | undefined>(undefined);

  useEffect(() => {
    const onHistoryEvent = () => {
      const newPath = location.pathname;
      if (!currentPath.current) {
        currentPath.current = newPath;
        return;
      }

      if (currentPath.current !== newPath) {
        currentPath.current = newPath;
        const properties = getProperties ? getProperties() : {};
        trackPage(properties);
      }
    };

    historyEvents.forEach((e) => addEventListener(e, onHistoryEvent));
    onHistoryEvent();

    return () => historyEvents.forEach((e) => removeEventListener(e, onHistoryEvent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export interface Properties {
  [index: string]: string | number | boolean | null | undefined | Properties | (string | number | boolean | Properties)[];
}

export const trackPage = (properties: Properties = {}) => {
  const analytics = getRudderAnalytics();

  analytics?.page(undefined, undefined, properties);
};

export const identifyUserId = (userId: string | number | bigint, traits?: Properties) => {
  const analytics = getRudderAnalytics();

  analytics?.identify(`${process.env.NODE_ENV}-user-${String(userId)}`, traits);
};

const eventData = (properties: Properties, area?: Area) => {
  let defaultProperties: Properties = {};

  if (area === "edit" && typeof window !== "undefined" && (window as any).GadgetEditConfig) {
    const { applicationId, environmentId, currentUser } = (window as any).GadgetEditConfig;

    defaultProperties = {
      application_id: applicationId,
      environment_id: environmentId,
      user_id: currentUser.id,
      user_email_sha256: currentUser.emailHash,
    };
  }

  return { ...defaultProperties, ...properties };
};

export const trackEvent = (name: string, properties: Properties, area?: Area) => {
  const analytics = getRudderAnalytics();
  const event = eventData(properties, area);
  if (analytics) {
    analytics.track(name, event);
  }
  if (typeof window !== "undefined" && (window as any).dataLayer) {
    (window as any).dataLayer.push({ event: name, ...event });
  }
};

// high volume event tracker that doesn't go through rudderstack and instead sends a beacon to the server
const HIGH_VOLUME_QUEUE: Record<string, unknown>[] = [];
const MAX_QUEUE_SIZE = 50;

const flushHighVolumeQueue = () => {
  if (HIGH_VOLUME_QUEUE.length === 0) return;

  const events = HIGH_VOLUME_QUEUE.splice(0, HIGH_VOLUME_QUEUE.length);
  const blob = new Blob([JSON.stringify({ events })], {
    type: "application/json;charset=UTF-8",
  });
  navigator.sendBeacon("/edit/api/events", blob);
};

const throttledFlushHighVolumeQueue = throttle(flushHighVolumeQueue, 10_000);

export const trackHighVolumeEvent = (name: string, properties: Properties) => {
  HIGH_VOLUME_QUEUE.push({ name, properties: eventData(properties, "edit") });
  if (HIGH_VOLUME_QUEUE.length >= MAX_QUEUE_SIZE) {
    flushHighVolumeQueue();
  } else {
    throttledFlushHighVolumeQueue();
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    flushHighVolumeQueue();
  });
}
